import React, { useEffect, useMemo, useRef, useState } from "react";
import SearchableDropdown from "../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx";
import { useSnackBar } from "../../FlowBuilder/Common/SnackBar/index.tsx";
import { useRecoilValue } from "recoil";
import useDebounce from "../../Shared/Delay/index.tsx";
import { FilterObj } from "../../../interfaces/Management.interface.ts";
import { userInformationAtom } from "../../../Services/_states/auth.js";
import { ManagementActions } from "../../../Services/_actions/index.js";

interface PaginatedResponse<T = any> {
    count: number;
    pages?: number;
    current_page?: number | string; // API returns "1" in your example
    has_next?: boolean;
    has_previous?: boolean;
    list: T[];
}

interface Role {
    id: number | string;
    name: string;
    codename: string;
    organisation: string;
}

interface RoleDropdownProps {
    value: any;
    onChange: (value: any) => void;
    organisationId?: string;
    error?: boolean;
    errorMessage?: string;
    label?: string;
    disabled?: boolean;
    sx?: any;
    multiSelect?: boolean;
}

const RoleDropdown: React.FC<RoleDropdownProps> = ({
    value,
    onChange,
    error = false,
    errorMessage = "",
    label = "Select Role",
    disabled = false,
    sx = { mb: 1, mt: 1 },
    organisationId = null,
    multiSelect = false,
}) => {
    const { showSnackBar } = useSnackBar();
    const actions = ManagementActions();
    const loggedInUser = useRecoilValue(userInformationAtom);

    // figure out effective organisation id
    const loggedInOrgId = loggedInUser?.profile?.organisation?.organisation_id;
    const effectiveOrgId = organisationId || loggedInOrgId;
    const [isLoading, setIsLoading] = useState(false);

    // keep our filters (page is 0-indexed for our internal use)
    const [filtersObj, setFiltersObj] = useState < FilterObj > ({
        limit: 20,
        search: "",
        page: 0,
        sortOrder: "desc",
        sortField: "created_at",
    });

    const debouncedSearch = useDebounce(filtersObj.search, 500);

    // hold combined list + server paging flags
    const [roleResponse, setRoleResponse] = useState < PaginatedResponse < Role >> ({
        count: 0,
        pages: 0,
        current_page: 0,
        has_next: false,
        has_previous: false,
        list: [],
    });

    // track an in-flight key to avoid race conditions (search fast typing)
    const inFlightKeyRef = useRef < string > ("");

    const makeKey = (orgId?: string, search = "", page = 0, limit = 20) =>
        JSON.stringify({ orgId, search, page, limit });

    const getRoleList = async (
        params: FilterObj & { organisation_id?: string },
        { append = false }: { append?: boolean } = {}
    ) => {
        // prevent fetch without org id
        if (!params.organisation_id) return;

        const key = makeKey(
            params.organisation_id,
            params.search,
            params.page,
            params.limit
        );
        inFlightKeyRef.current = key;
        setIsLoading(true);
        try {
            const res = await actions.getRoleList(params);
            const incoming: PaginatedResponse<Role> =
                res?.status === 200 ? res?.data || {} : {};

            // server current_page might be 1-based; normalize
            const apiCurrent = Number(incoming.current_page ?? 0);
            const normalizedCurrent = isNaN(apiCurrent) ? 0 : apiCurrent;

            // respect latest request only
            if (inFlightKeyRef.current !== key) return;

            setRoleResponse((prev) => {
                const nextList = append ? [...(prev?.list || []), ...(incoming.list || [])] : incoming.list || [];
                return {
                    count: incoming.count ?? (append ? prev.count : 0),
                    pages: incoming.pages,
                    current_page: normalizedCurrent,
                    has_next: incoming.has_next,
                    has_previous: incoming.has_previous,
                    list: nextList,
                };
            });
        } catch (e) {
            showSnackBar("Error fetching roles", "error");
        } finally {
            // only end loading if this request is the latest
            if (inFlightKeyRef.current === key) setIsLoading(false);
        }
    };

    // initial + on organisation change -> reset & load page 0
    useEffect(() => {
        if (!effectiveOrgId) return;
        // reset state on org change
        setRoleResponse({
            count: 0,
            pages: 0,
            current_page: 0,
            has_next: false,
            has_previous: false,
            list: [],
        });
        const nextFilters = { ...filtersObj, page: 0 };
        setFiltersObj(nextFilters);
        getRoleList({ ...nextFilters, organisation_id: effectiveOrgId }, { append: false });
    }, [effectiveOrgId]);

    // on search (debounced) -> reset to page 0 and fetch
    useEffect(() => {
        if (!effectiveOrgId && effectiveOrgId !== "") return;
        const nextFilters = { ...filtersObj, page: 0, search: debouncedSearch ?? "" };
        setFiltersObj(nextFilters);
        getRoleList({ ...nextFilters, organisation_id: effectiveOrgId }, { append: false });
    }, [debouncedSearch]);

    // load more handler for the dropdown (infinite scroll)
    const loadMoreRoles = async () => {
        if (isLoading) return;
        if (!roleResponse?.has_next) return; // nothing more to load

        const nextPage =
            typeof roleResponse.current_page === "number"
                ? roleResponse.current_page + 1
                : Number(roleResponse.current_page) + 1;

        const nextFilters = { ...filtersObj, page: nextPage };
        setFiltersObj(nextFilters);

        await getRoleList(
            { ...nextFilters, organisation_id: effectiveOrgId },
            { append: true }
        );
    };

    const handleSearch = (text: string) => {
        // only update local search; effect on debouncedSearch drives fetch
        setFiltersObj((prev) => ({ ...prev, search: text, page: 0 }));
    };

    return (
        <SearchableDropdown
            options={roleResponse?.list || []}
            label={label}
            value={value}
            onChange={onChange}
            fetchMoreOptions={loadMoreRoles}
            onSearch={handleSearch}
            labelKey="name"
            valueKey="id"
            loading={isLoading}
            error={error}
            errorMessage={errorMessage}
            disabled={disabled}
            sx={sx}
            multiSelect={multiSelect ?? false}
        />
    );
};

export default RoleDropdown;
